import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Icon,
  Text,
  BlockStack,
  InlineStack,
  Card,
  Badge,
} from '@shopify/polaris';
import {
  ChevronLeftMinor,
  ChevronRightMinor,
  ImageMajor,
} from '@shopify/polaris-icons';

const ViewTrialModal = ({ 
  isOpen, 
  onClose, 
  selectedTrial,
  setToastMessage, 
  setToastActive 
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentOutputIndex, setCurrentOutputIndex] = useState(0);

  // Reset image index when modal opens or trial changes
  useEffect(() => {
    if (isOpen) {
      setCurrentImageIndex(0);
      setCurrentOutputIndex(0);
    }
  }, [isOpen, selectedTrial]);

  const handleClose = () => {
    onClose();
    setCurrentImageIndex(0);
    setCurrentOutputIndex(0);
  };

  if (!selectedTrial) {
    return null;
  }

  const kidImages = selectedTrial.kid_images || [];
  const hasMultipleImages = kidImages.length > 1;

  // Collect all output URLs
  const outputImages = [];
  if (selectedTrial.output) {
    outputImages.push(selectedTrial.output);
  }
  if (selectedTrial.scoring_details && selectedTrial.scoring_details.length > 0) {
    selectedTrial.scoring_details.forEach(detail => {
      if (detail.output_url && !outputImages.includes(detail.output_url)) {
        outputImages.push(detail.output_url);
      }
    });
  }
  const hasMultipleOutputs = outputImages.length > 1;

  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : kidImages.length - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev < kidImages.length - 1 ? prev + 1 : 0));
  };

  const handlePreviousOutput = () => {
    setCurrentOutputIndex((prev) => (prev > 0 ? prev - 1 : outputImages.length - 1));
  };

  const handleNextOutput = () => {
    setCurrentOutputIndex((prev) => (prev < outputImages.length - 1 ? prev + 1 : 0));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const datePart = date.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric', 
        timeZone: 'Africa/Cairo' 
      });
      let timePart = date.toLocaleTimeString('en-GB', { 
        hour: '2-digit', 
        minute: '2-digit', 
        hour12: true, 
        timeZone: 'Africa/Cairo' 
      });
      timePart = timePart.replace(/am|pm/i, match => match.toUpperCase());
      return `${datePart} ${timePart}`;
    } catch {
      return '-';
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={`Trial: ${selectedTrial.kid_name}`}
      size="large"
      secondaryActions={[
        {
          content: 'Close',
          onAction: handleClose,
        },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {/* Trial Information */}
          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd">Trial Information</Text>
              
              <InlineStack gap="400" wrap={false}>
                <div style={{ flex: 1 }}>
                  <Text variant="bodyMd" fontWeight="medium">Trial ID</Text>
                  <Text variant="bodySm" tone="subdued">
                    {selectedTrial.trial_id || selectedTrial._id || '-'}
                  </Text>
                </div>
                
                <div style={{ flex: 1 }}>
                  <Text variant="bodyMd" fontWeight="medium">Kid Name</Text>
                  <Text variant="bodySm" tone="subdued">
                    {selectedTrial.kid_name || '-'}
                  </Text>
                </div>
                
                <div style={{ flex: 1 }}>
                  <Text variant="bodyMd" fontWeight="medium">Story</Text>
                  <Text variant="bodySm" tone="subdued">
                    {selectedTrial.story || '-'}
                  </Text>
                </div>
              </InlineStack>

              <InlineStack gap="400" wrap={false}>
                <div style={{ flex: 1 }}>
                  <Text variant="bodyMd" fontWeight="medium">Status</Text>
                  <div style={{ marginTop: '4px' }}>
                    <Badge tone={selectedTrial.status === 'completed' ? 'success' : 'attention'}>
                      {selectedTrial.status || 'Pending'}
                    </Badge>
                  </div>
                </div>
                
                <div style={{ flex: 1 }}>
                  <Text variant="bodyMd" fontWeight="medium">Created Date</Text>
                  <Text variant="bodySm" tone="subdued">
                    {formatDate(selectedTrial.created_at)}
                  </Text>
                </div>
                
                <div style={{ flex: 1 }}>
                  <Text variant="bodyMd" fontWeight="medium">Images Count</Text>
                  <Text variant="bodySm" tone="subdued">
                    {kidImages.length} image{kidImages.length !== 1 ? 's' : ''}
                  </Text>
                </div>
              </InlineStack>

              <InlineStack gap="400" wrap={false}>
                <div style={{ flex: 1 }}>
                  <Text variant="bodyMd" fontWeight="medium">Order ID</Text>
                  <Text variant="bodySm" tone="subdued">
                    {selectedTrial.order_id || '-'}
                  </Text>
                </div>
                
                <div style={{ flex: 1 }}>
                  <Text variant="bodyMd" fontWeight="medium">Total Score</Text>
                  <Text variant="bodySm" tone="subdued">
                    {selectedTrial.total_score ? `${selectedTrial.total_score}%` : '-'}
                  </Text>
                </div>
                
                <div style={{ flex: 1 }}>
                  <Text variant="bodyMd" fontWeight="medium">Regenerations</Text>
                  <Text variant="bodySm" tone="subdued">
                    {selectedTrial.regeneration_count || 0}
                  </Text>
                </div>
              </InlineStack>
            </BlockStack>
          </Card>

          {/* Kid Images */}
          {kidImages.length > 0 && (
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between">
                  <Text variant="headingMd">Kid Images</Text>
                  {hasMultipleImages && (
                    <Text variant="bodySm" tone="subdued">
                      {currentImageIndex + 1} of {kidImages.length}
                    </Text>
                  )}
                </InlineStack>
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  position: 'relative',
                  minHeight: '300px'
                }}>
                  {hasMultipleImages && (
                    <Button
                      icon={ChevronLeftMinor}
                      onClick={handlePreviousImage}
                      variant="tertiary"
                      style={{ 
                        position: 'absolute', 
                        left: '10px', 
                        zIndex: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: '50%'
                      }}
                    />
                  )}
                  
                  <div style={{ 
                    maxWidth: '400px', 
                    maxHeight: '400px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <img
                      src={kidImages[currentImageIndex]}
                      alt={`${selectedTrial.kid_name} ${currentImageIndex + 1}`}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div style={{ 
                      display: 'none',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#6d7175'
                    }}>
                      <Icon source={ImageMajor} />
                      <Text variant="bodySm" tone="subdued">
                        Image not available
                      </Text>
                    </div>
                  </div>
                  
                  {hasMultipleImages && (
                    <Button
                      icon={ChevronRightMinor}
                      onClick={handleNextImage}
                      variant="tertiary"
                      style={{ 
                        position: 'absolute', 
                        right: '10px', 
                        zIndex: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: '50%'
                      }}
                    />
                  )}
                </div>

                {/* Image thumbnails for multiple images */}
                {hasMultipleImages && (
                  <div style={{ 
                    display: 'flex', 
                    gap: '8px', 
                    justifyContent: 'center',
                    flexWrap: 'wrap'
                  }}>
                    {kidImages.map((imageUrl, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        style={{
                          border: currentImageIndex === index ? '2px solid #008060' : '2px solid transparent',
                          borderRadius: '6px',
                          padding: '2px',
                          background: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <img
                          src={imageUrl}
                          alt={`Thumbnail ${index + 1}`}
                          style={{
                            width: '50px',
                            height: '50px',
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </BlockStack>
            </Card>
          )}

          {/* Trial Output */}
          {outputImages.length > 0 && (
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between">
                  <Text variant="headingMd">Trial Output</Text>
                  {hasMultipleOutputs && (
                    <Text variant="bodySm" tone="subdued">
                      {currentOutputIndex + 1} of {outputImages.length}
                    </Text>
                  )}
                </InlineStack>
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  position: 'relative',
                  minHeight: '300px'
                }}>
                  {hasMultipleOutputs && (
                    <Button
                      icon={ChevronLeftMinor}
                      onClick={handlePreviousOutput}
                      variant="tertiary"
                      style={{ 
                        position: 'absolute', 
                        left: '10px', 
                        zIndex: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: '50%'
                      }}
                    />
                  )}
                  
                  <div style={{ 
                    maxWidth: '400px', 
                    maxHeight: '400px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <img
                      src={outputImages[currentOutputIndex]}
                      alt={`Trial output ${currentOutputIndex + 1} for ${selectedTrial.kid_name}`}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div style={{ 
                      display: 'none',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#6d7175'
                    }}>
                      <Icon source={ImageMajor} />
                      <Text variant="bodySm" tone="subdued">
                        Output not available
                      </Text>
                    </div>
                  </div>
                  
                  {hasMultipleOutputs && (
                    <Button
                      icon={ChevronRightMinor}
                      onClick={handleNextOutput}
                      variant="tertiary"
                      style={{ 
                        position: 'absolute', 
                        right: '10px', 
                        zIndex: 1,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: '50%'
                      }}
                    />
                  )}
                </div>

                {/* Output thumbnails for multiple images */}
                {hasMultipleOutputs && (
                  <div style={{ 
                    display: 'flex', 
                    gap: '8px', 
                    justifyContent: 'center',
                    flexWrap: 'wrap'
                  }}>
                    {outputImages.map((imageUrl, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentOutputIndex(index)}
                        style={{
                          border: currentOutputIndex === index ? '2px solid #008060' : '2px solid transparent',
                          borderRadius: '6px',
                          padding: '2px',
                          background: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <img
                          src={imageUrl}
                          alt={`Output thumbnail ${index + 1}`}
                          style={{
                            width: '50px',
                            height: '50px',
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </BlockStack>
            </Card>
          )}

          {/* Scoring Details */}
          {selectedTrial.scoring_details && selectedTrial.scoring_details.length > 0 && (
            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd">Scoring Details</Text>
                
                {selectedTrial.scoring_details.map((score, index) => (
                  <Card key={index} sectioned>
                    <BlockStack gap="200">
                      <InlineStack align="space-between">
                        <Text variant="bodyMd" fontWeight="medium">
                          Attempt {score.attempt || index + 1}
                        </Text>
                        <Badge tone={score.pass ? 'success' : 'critical'}>
                          {score.pass ? 'Passed' : 'Failed'}
                        </Badge>
                      </InlineStack>
                      
                      <InlineStack gap="400" wrap>
                        <div>
                          <Text variant="bodySm" fontWeight="medium">Face Similarity</Text>
                          <Text variant="bodySm" tone="subdued">{score.face_similarity || '-'}%</Text>
                        </div>
                        <div>
                          <Text variant="bodySm" fontWeight="medium">Expression Match</Text>
                          <Text variant="bodySm" tone="subdued">{score.expression_match || '-'}%</Text>
                        </div>
                        <div>
                          <Text variant="bodySm" fontWeight="medium">Hairstyle Match</Text>
                          <Text variant="bodySm" tone="subdued">{score.hairstyle_match || '-'}%</Text>
                        </div>
                        <div>
                          <Text variant="bodySm" fontWeight="medium">Body Proportion</Text>
                          <Text variant="bodySm" tone="subdued">{score.body_proportion_match || '-'}%</Text>
                        </div>
                      </InlineStack>
                      
                      <InlineStack gap="400" wrap>
                        <div>
                          <Text variant="bodySm" fontWeight="medium">Skin Tone Match</Text>
                          <Text variant="bodySm" tone="subdued">{score.skin_tone_match || '-'}%</Text>
                        </div>
                        <div>
                          <Text variant="bodySm" fontWeight="medium">Cartoon Quality</Text>
                          <Text variant="bodySm" tone="subdued">{score.overall_cartoon_quality || '-'}%</Text>
                        </div>
                        <div>
                          <Text variant="bodySm" fontWeight="medium">Total Score</Text>
                          <Text variant="bodySm" tone="subdued" fontWeight="medium">{score.total_score || '-'}%</Text>
                        </div>
                        <div>
                          <Text variant="bodySm" fontWeight="medium">Timestamp</Text>
                          <Text variant="bodySm" tone="subdued">{formatDate(score.timestamp)}</Text>
                        </div>
                      </InlineStack>
                    </BlockStack>
                  </Card>
                ))}
              </BlockStack>
            </Card>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default ViewTrialModal;
